import React, { useEffect, useState } from 'react'
import { FormControlLabel, Grid, Switch } from '@material-ui/core'
import { Controller, useForm } from 'react-hook-form'

import { Label, LoadingFeedback } from 'components'
import { DocumentConfigurationDialog } from './components'

import useDocumentGenerate from 'hooks/useDocumentGenerate'
import useSnackbar from 'hooks/useSnackbar'

import * as service from 'service'
import helpers from 'helpers'
import constants from 'constants/index'

const ConfigurationStep = () => {
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false)

  const { AWAITING_APPROVAL_STATUS_ID, ACTIVE_STATUS_ID } = constants.documents

  const snackbar = useSnackbar()
  const {
    documentGenerated,
    handleNext,
    resetAdvanceStep,
    setDisabledNextButton,
    setDocumentGenerated,
    wantsToAdvanceStep,
    documentTemplate,
  } = useDocumentGenerate()

  const { control, handleSubmit, watch, setValue } = useForm({
    defaultValues: {
      publicDocument: documentGenerated?.publicDocument,
      allowAutomaticEditing: documentGenerated?.allowAutomaticEditing,
      collectAccepted: documentGenerated?.collectAccepted,
      checkOldVersions: documentGenerated?.checkOldVersions,
    },
  })

  const publicDocument = watch('publicDocument')

  useEffect(() => {
    if (
      publicDocument === true &&
      (documentGenerated?.publicDocument === false ||
        documentGenerated?.publicDocument === null)
    ) {
      setOpen(true)
    }
  }, [publicDocument])

  const handleCancelDialog = () => {
    setValue('publicDocument', false)
    setOpen(false)
  }

  const handleContinueDialog = () => {
    setOpen(false)
  }

  const status = documentGenerated?.collectApproval
    ? AWAITING_APPROVAL_STATUS_ID
    : ACTIVE_STATUS_ID

  const onSubmit = async (data) => {
    try {
      setLoading(true)

      const response = await service.dponet.privacyPolicies.put({
        privacyPolicyId: documentGenerated?.id,
        publicDocument: data?.publicDocument,
        allowAutomaticEditing: data?.allowAutomaticEditing,
        collectAccepted: data?.collectAccepted,
        checkOldVersions: data?.checkOldVersions,
        status: status,
        code: documentTemplate?.code,
      })
      const privacyPolicy = response.data?.privacyPolicy ?? documentGenerated

      const approvalEmails = documentGenerated?.approvalEmails
      if (approvalEmails?.emails?.length > 0) {
        await service.dponet.privacyPolicies.sendLink({
          privacyPolicyId: documentGenerated?.id,
          email: approvalEmails.emails,
        })
      }

      setDocumentGenerated(privacyPolicy)
      handleNext()
    } catch (error) {
      snackbar.open({
        message: helpers.formatters.errorMessage(error),
        variant: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    setDisabledNextButton(false)
  }, [])

  useEffect(() => {
    if (wantsToAdvanceStep) {
      handleSubmit(onSubmit)()
      resetAdvanceStep()
    }
  }, [wantsToAdvanceStep])

  return (
    <Grid container spacing={2}>
      <LoadingFeedback open={loading} />

      <DocumentConfigurationDialog
        open={open}
        setOpen={setOpen}
        actionButton={handleContinueDialog}
        closeButtonAction={handleCancelDialog}
      />
      <Grid item xs={12}>
        <Label
          title="Publicar documento"
          description="Exibir documento no Portal da Privacidade (Privacidade & Você), permitindo que ele fique disponível para consulta pública pelos titulares de dados."
          paddingBoxTitle={0}
        >
          <Controller
            as={
              <FormControlLabel
                label="Publicar este documento no seu canal de atendimento ao titular de dados."
                labelPlacement="end"
                control={<Switch color="primary" />}
              />
            }
            name="publicDocument"
            control={control}
          />
        </Label>
      </Grid>
      <Grid item xs={12}>
        <Label
          title="Atualizações automáticas"
          description="Permite que o documento seja atualizado automaticamente sempre que a versão original for alterada pelo administrador do sistema."
          paddingBoxTitle={0}
        >
          <Controller
            as={
              <FormControlLabel
                label="Atualizar o documento automaticamente de acordo com o modelo de documento."
                labelPlacement="end"
                control={<Switch color="primary" />}
              />
            }
            name="allowAutomaticEditing"
            control={control}
          />
        </Label>
      </Grid>
      <Grid item xs={12}>
        <Label
          title="Coletar aceites"
          description="Permite a coleta de aceites do documento e outros dados opcionais dos usuários."
          paddingBoxTitle={0}
        >
          <Controller
            as={
              <FormControlLabel
                label="Coletar aceites dos termos desse documento."
                labelPlacement="end"
                control={<Switch color="primary" />}
              />
            }
            name="collectAccepted"
            control={control}
          />
        </Label>
      </Grid>
      <Grid item xs={12}>
        <Label
          title="Mostrar versões"
          description="Permite a visualização de versões anteriores do documento através do histórico de versões"
          paddingBoxTitle={0}
        >
          <Controller
            as={
              <FormControlLabel
                label="Permite a visualização de versões anteriores do documento através do histórico de versões"
                labelPlacement="end"
                control={<Switch color="primary" />}
              />
            }
            name="checkOldVersions"
            control={control}
          />
        </Label>
      </Grid>
    </Grid>
  )
}

export default ConfigurationStep
